import React, { FormEvent, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Form, Spinner as BootstrapSpinner } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faChevronRight,
  faCheck,
  faEye,
  faEyeSlash,
  faPlus,
  faTrash,
  faWallet,
} from '@fortawesome/free-solid-svg-icons';
import { ConfirmationModal } from '../../components/UnifiedModal';
import AccountSettingsLoadingState from '../AccountSettings/AccountSettingsLoadingState';
import BrokerAccountRenameControl from '../BrokerAccounts/BrokerAccountRenameControl';
import useUser from '../../hooks/useUser';
import {
  createBrokerCredential,
  deleteBrokerCredential,
  listBrokerCredentials,
  listBrokerSnapshots,
  startBrokerOAuth,
  updateBrokerCredential,
} from '../../utils/savedSecrets';
import { ALL_BROKER_CONNECTIONS, findBrokerConnection } from '../../utils/brokerConnectionCatalog';
import { getBrokerAccountLabel, getBrokerConnectionTitle } from '../../utils/brokerConnectionDisplay';
import {
  brokerConnectionNeedsAttention,
  getBrokerConnectionStatusLabel,
  getBrokerConnectionWarning,
} from '../../utils/brokerConnectionStatus';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import { getErrorMessage } from '../../utils/errorMessage';
import './BrokerConnectionsPage.css';

const SETTINGS_BROKER_CONNECTIONS_PATH = '/account-settings/broker-connections';

type BrokerMode = 'paper' | 'live' | string;
type BrokerAuthMethod = 'oauth' | 'api_key' | string;
type FlowStep = 'choose' | 'details';
type ViewMode = 'list' | 'form';
type CredentialValues = Record<string, string>;
type NumericValue = number | string | null | undefined;

interface BrokerCredentialField {
  key: string;
  label: string;
  secret?: boolean;
  multiline?: boolean;
}

interface BrokerConnectionDefinition {
  id: string;
  name: string;
  logoPath?: string;
  logoText?: string;
  tags: string[];
  modes: BrokerMode[];
  modesByAuthMethod?: Record<string, BrokerMode[]>;
  authMethods: BrokerAuthMethod[];
  promoted?: boolean;
  warning?: string;
  referralNote?: string;
  referralOffer?: {
    headline: string;
    description: string;
    url: string;
    ctaLabel: string;
    verifiedAt?: string;
  } | null;
  credentialFields: BrokerCredentialField[];
  providerMetadata?: Record<string, unknown>;
}

interface BrokerCredentialItem {
  id: string;
  broker: string;
  displayName?: string | null;
  paperOrLive?: BrokerMode | null;
  authMethod?: BrokerAuthMethod | null;
  externalAccountId?: string | null;
  externalAccountLabel?: string | null;
  status?: string | null;
  connectionStatus?: string | null;
  connectionStatusReason?: string | null;
  reconnectRecommended?: boolean;
  connectionUpdatedAt?: string | null;
}

interface BrokerSnapshotAccount {
  latestPortfolioValue?: NumericValue;
  portfolioValue?: NumericValue;
  portfolio_value?: NumericValue;
  value?: NumericValue;
  latestCash?: NumericValue;
  latest_cash?: NumericValue;
  cash?: NumericValue;
  cashBalance?: NumericValue;
  cash_balance?: NumericValue;
}

interface BrokerSnapshotItem {
  brokerCredentialId: string;
  account?: BrokerSnapshotAccount | null;
  latestPortfolioValue?: NumericValue;
  portfolioValue?: NumericValue;
  portfolio_value?: NumericValue;
  latest_portfolio_value?: NumericValue;
  latestCash?: NumericValue;
  latest_cash?: NumericValue;
  cash?: NumericValue;
  cashBalance?: NumericValue;
  cash_balance?: NumericValue;
}

interface BrokerSnapshotListResponse {
  items?: BrokerSnapshotItem[];
}

interface BrokerSnapshotResult {
  response?: BrokerSnapshotListResponse;
  error?: unknown;
}

interface BrokerOAuthResponse {
  authorizeUrl?: string;
}

interface BrokerCredentialPayload {
  broker: string;
  displayName: string;
  paperOrLive: BrokerMode;
  authMethod: BrokerAuthMethod;
  externalAccountId: string | null;
  externalAccountLabel: string | null;
  providerMetadata: Record<string, unknown>;
  replaceExisting: boolean;
  credentialValues?: CredentialValues;
}

const getBrokerDefinition = (id?: string | null): BrokerConnectionDefinition =>
  findBrokerConnection(id || undefined) as BrokerConnectionDefinition;

const brokerCatalog = ALL_BROKER_CONNECTIONS as BrokerConnectionDefinition[];
const brokerAssetTags = new Set(['Stocks', 'Options', 'Crypto', 'Futures']);

function getBrokerAssetTags(broker: BrokerConnectionDefinition): string[] {
  return broker.tags.filter((tag) => brokerAssetTags.has(tag));
}

const emptyCredentialValues = (broker: BrokerConnectionDefinition): CredentialValues => Object.fromEntries(
  (broker?.credentialFields || []).map((field) => [field.key, ''])
) as CredentialValues;

function buildDisplayName(broker: BrokerConnectionDefinition, mode?: BrokerMode | null): string {
  return [broker.name, mode].filter(Boolean).join(' ');
}

function formatAccountMode(mode?: BrokerMode | null): string {
  return mode === 'paper' ? 'Paper account' : 'Live account';
}

function getBrokerModesForAuthMethod(
  broker: BrokerConnectionDefinition,
  authMethod: BrokerAuthMethod,
): BrokerMode[] {
  return broker?.modesByAuthMethod?.[authMethod] || broker?.modes || ['live'];
}

function defaultModeForBrokerAuthMethod(
  broker: BrokerConnectionDefinition,
  authMethod: BrokerAuthMethod,
): BrokerMode {
  const modes = getBrokerModesForAuthMethod(broker, authMethod);
  return modes.includes('paper') ? 'paper' : modes[0] || 'live';
}

function normalizedModeForBrokerAuthMethod(
  broker: BrokerConnectionDefinition,
  authMethod: BrokerAuthMethod,
  requestedMode?: BrokerMode | null,
): BrokerMode {
  const modes = getBrokerModesForAuthMethod(broker, authMethod);
  if (broker?.id === 'tradier' && authMethod === 'oauth') return 'live';
  return requestedMode && modes.includes(requestedMode) ? requestedMode : defaultModeForBrokerAuthMethod(broker, authMethod);
}

function getBrokerInitials(name = ''): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function renderBrokerAvatar(
  broker: BrokerConnectionDefinition,
  className = 'broker-connections-page__avatar',
): React.ReactElement {
  if (broker.logoPath) {
    return (
      <span className={`${className} has-logo`} aria-hidden="true">
        <img
          src={broker.logoPath}
          alt=""
          onError={(event: SyntheticEvent<HTMLImageElement>) => {
            event.currentTarget.closest('.broker-connections-page__avatar')?.classList.add('logo-failed');
          }}
        />
        <span className="broker-connections-page__avatar-fallback">{getBrokerInitials(broker.name)}</span>
      </span>
    );
  }

  if (broker.logoText) {
    return (
      <span className={`${className} has-wordmark`} aria-hidden="true">
        {broker.logoText}
      </span>
    );
  }

  return (
    <span className={className} aria-hidden="true">
      {getBrokerInitials(broker.name)}
    </span>
  );
}

function brokerConnectionLoadError(err: unknown): string {
  const message = getErrorMessage(err, '');
  if (message && /networkerror|failed to fetch/i.test(message)) {
    return 'Could not load broker connections. Refresh the page and try again.';
  }
  return message || 'Failed to load broker connections.';
}

function readFiniteNumber(...values: NumericValue[]): number | null {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    const numeric = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
}

function formatMoney(value: NumericValue, fallback = 'No value yet'): string {
  if (value === null || value === undefined || value === '') return fallback;
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(numeric);
}

function formatConnectionUpdatedAt(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function getSnapshotAccountValues(snapshot?: BrokerSnapshotItem): { accountValue: number | null; cash: number | null } {
  const account = snapshot?.account || {};
  return {
    accountValue: readFiniteNumber(
      account.latestPortfolioValue,
      account.portfolioValue,
      account.portfolio_value,
      account.value,
      snapshot?.latestPortfolioValue,
      snapshot?.portfolioValue,
      snapshot?.portfolio_value,
      snapshot?.latest_portfolio_value
    ),
    cash: readFiniteNumber(
      account.latestCash,
      account.latest_cash,
      account.cash,
      account.cashBalance,
      account.cash_balance,
      snapshot?.latestCash,
      snapshot?.latest_cash,
      snapshot?.cash,
      snapshot?.cashBalance,
      snapshot?.cash_balance
    ),
  };
}

function safeInternalPath(value: string | null, fallback = SETTINGS_BROKER_CONNECTIONS_PATH): string {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')
    ? value
    : fallback;
}

export default function BrokerConnectionsPage() {
  const { getApiToken } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = safeInternalPath(searchParams.get('returnTo'));
  const editId = searchParams.get('editId');
  const requestedAction = searchParams.get('action');
  const brokerConnected = searchParams.get('brokerConnected');
  const brokerOauthError = searchParams.get('brokerOauthError');
  const brokerOauthProvider = searchParams.get('brokerOauthProvider');
  const [items, setItems] = useState<BrokerCredentialItem[]>([]);
  const [snapshotItems, setSnapshotItems] = useState<BrokerSnapshotItem[]>([]);
  const requestedBrokerId = searchParams.get('broker');
  const requestedAuthMethod = searchParams.get('authMethod');
  const initialBrokerId = getBrokerDefinition(requestedBrokerId).id || 'alpaca';
  const initialBroker = getBrokerDefinition(initialBrokerId);
  const initialAuthMethod = requestedAuthMethod && initialBroker.authMethods.includes(requestedAuthMethod)
    ? requestedAuthMethod
    : initialBroker.authMethods.includes('oauth')
    ? 'oauth'
    : initialBroker.authMethods[0];
  const [selectedBrokerId, setSelectedBrokerId] = useState(initialBrokerId);
  const selectedBroker = useMemo(() => getBrokerDefinition(selectedBrokerId), [selectedBrokerId]);
  const [paperOrLive, setPaperOrLive] = useState(() => defaultModeForBrokerAuthMethod(initialBroker, initialAuthMethod));
  const [authMethod, setAuthMethod] = useState(initialAuthMethod);
  const availableModes = useMemo(
    () => getBrokerModesForAuthMethod(selectedBroker, authMethod),
    [authMethod, selectedBroker]
  );
  const fixedAccountMode = availableModes.length <= 1;
  const [displayName, setDisplayName] = useState('');
  const [credentialValues, setCredentialValues] = useState(() => emptyCredentialValues(selectedBroker));
  const [acceptedDisclosure, setAcceptedDisclosure] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [flowStep, setFlowStep] = useState<FlowStep>(editId || requestedBrokerId ? 'details' : 'choose');
  const [viewMode, setViewMode] = useState<ViewMode>(
    editId || requestedBrokerId || requestedAction === 'add' ? 'form' : 'list'
  );
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [reconnectingId, setReconnectingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<BrokerCredentialItem | null>(null);
  const [revealedSecrets, setRevealedSecrets] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    const broker = getBrokerDefinition(requestedBrokerId);
    const nextAuthMethod = requestedAuthMethod && broker.authMethods.includes(requestedAuthMethod)
      ? requestedAuthMethod
      : broker.authMethods.includes('oauth')
      ? 'oauth'
      : broker.authMethods[0];
    setSelectedBrokerId(broker.id);
    setAuthMethod(nextAuthMethod);
    setPaperOrLive(defaultModeForBrokerAuthMethod(broker, nextAuthMethod));
    setDisplayName('');
    setCredentialValues(emptyCredentialValues(broker));
    setRevealedSecrets({});
    setAcceptedDisclosure(false);
    setEditingId(null);
    setFlowStep(requestedBrokerId ? 'details' : 'choose');
  }, [requestedAuthMethod, requestedBrokerId]);

  const beginAdd = useCallback(() => {
    resetForm();
    setViewMode('form');
    setError(null);
  }, [resetForm]);

  const closeForm = useCallback(() => {
    resetForm();
    setViewMode('list');
    setError(null);
    navigate(SETTINGS_BROKER_CONNECTIONS_PATH, { replace: true });
  }, [navigate, resetForm]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getApiToken();
      const [credentialItems, snapshotResult] = await Promise.all([
        listBrokerCredentials(token),
        listBrokerSnapshots(token, { includeLatest: true })
          .then((response: BrokerSnapshotListResponse) => ({ response }))
          .catch((err: unknown) => ({ error: err })),
      ]);
      const brokerSnapshotResult = snapshotResult as BrokerSnapshotResult;
      setItems(credentialItems as BrokerCredentialItem[]);
      setSnapshotItems(brokerSnapshotResult.error ? [] : brokerSnapshotResult.response?.items || []);
    } catch (err) {
      setError(brokerConnectionLoadError(err));
    } finally {
      setLoading(false);
    }
  }, [getApiToken]);

  const snapshotByCredentialId = useMemo(
    () => new Map<string, BrokerSnapshotItem>(snapshotItems.map((snapshot) => [snapshot.brokerCredentialId, snapshot])),
    [snapshotItems]
  );

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (requestedAction === 'add' || requestedBrokerId) {
      resetForm();
      setViewMode('form');
      return;
    }
    if (editId) {
      setViewMode('form');
      setFlowStep('details');
    }
  }, [editId, requestedAction, requestedBrokerId, resetForm]);

  useEffect(() => {
    if (brokerConnected) {
      const broker = getBrokerDefinition(brokerConnected);
      setError(null);
      loadItems();
      pushEvent({
        event: ANALYTICS_EVENTS.BROKER_CONNECTION_SAVED || 'broker_connection_saved',
        broker: brokerConnected,
        authMethod: 'oauth',
        source: 'account_settings',
      });
      return;
    }
    if (brokerOauthError) {
      const broker = getBrokerDefinition(brokerOauthProvider);
      setError(`${broker?.name || 'Broker'} sign-in did not finish. Please try again.`);
      pushEvent({
        event: 'broker_connection_oauth_failed',
        broker: brokerOauthProvider || 'unknown',
        reason: brokerOauthError,
        source: 'account_settings',
      });
    }
  }, [brokerConnected, brokerOauthError, brokerOauthProvider, loadItems]);

  useEffect(() => {
    if (editingId) return;
    setCredentialValues(emptyCredentialValues(selectedBroker));
    setRevealedSecrets({});
    const nextAuthMethod = requestedAuthMethod && selectedBroker.authMethods.includes(requestedAuthMethod)
      ? requestedAuthMethod
      : selectedBroker.authMethods.includes('oauth')
      ? 'oauth'
      : selectedBroker.authMethods[0];
    setAuthMethod(nextAuthMethod);
    setPaperOrLive(defaultModeForBrokerAuthMethod(selectedBroker, nextAuthMethod));
    setDisplayName('');
    setAcceptedDisclosure(false);
  }, [editingId, requestedAuthMethod, selectedBroker]);

  useEffect(() => {
    if (availableModes.includes(paperOrLive)) return;
    setPaperOrLive(defaultModeForBrokerAuthMethod(selectedBroker, authMethod));
  }, [authMethod, availableModes, paperOrLive, selectedBroker]);

  const setCredentialValue = (key: string, value: string): void => {
    setCredentialValues((current) => ({ ...current, [key]: value }));
  };

  const toggleSecretReveal = (key: string): void => {
    setRevealedSecrets((current) => ({ ...current, [key]: !current[key] }));
  };

  const selectBrokerForSetup = (brokerId: string): void => {
    setSelectedBrokerId(brokerId);
    setFlowStep('details');
  };

  const startOAuthConnection = async (): Promise<void> => {
    try {
      setError(null);
      if (!acceptedDisclosure) {
        setError('Accept the broker connection terms before continuing.');
        return;
      }
      setSaving(true);
      const token = await getApiToken();
      const connectionMode = normalizedModeForBrokerAuthMethod(selectedBroker, authMethod, paperOrLive);
      const response = await startBrokerOAuth(token, selectedBroker.id, {
        displayName: displayName.trim(),
        paperOrLive: connectionMode,
        returnTo,
      }) as BrokerOAuthResponse;
      if (!response?.authorizeUrl) {
        throw new Error('Broker sign-in is not configured for this environment.');
      }
      pushEvent({
        event: 'broker_connection_oauth_started',
        broker: selectedBroker.id,
        paperOrLive: connectionMode,
        source: 'account_settings',
      });
      window.location.href = response.authorizeUrl;
    } catch (err) {
      setError(getErrorMessage(err, `Unable to start ${selectedBroker.name} OAuth.`));
      setSaving(false);
    }
  };

  const beginEdit = useCallback((item: BrokerCredentialItem) => {
    const broker = getBrokerDefinition(item.broker);
    setSelectedBrokerId(item.broker);
    setPaperOrLive(item.paperOrLive || 'paper');
    setAuthMethod(item.authMethod || 'api_key');
    setDisplayName(item.displayName || '');
    setCredentialValues(emptyCredentialValues(broker));
    setRevealedSecrets({});
    setEditingId(item.id);
    setFlowStep('details');
    setViewMode('form');
    setAcceptedDisclosure(true);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!editId || items.length === 0 || editingId === editId) return;
    const item = items.find((candidate) => candidate.id === editId);
    if (item) beginEdit(item);
  }, [beginEdit, editId, editingId, items]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!acceptedDisclosure) {
      setError('Accept the broker connection terms before saving.');
      return;
    }

    const credentialPayload = Object.fromEntries(
      Object.entries(credentialValues).filter(([, value]) => String(value || '').trim())
    ) as CredentialValues;
    if (!editingId && Object.keys(credentialPayload).length === 0) {
      setError('Enter the broker credential values before saving.');
      return;
    }

    const providerMetadata = {
      ...(selectedBroker.providerMetadata || {}),
      connectionSource: 'account_settings',
    };
    const externalAccountId = credentialPayload.TRADIER_ACCOUNT_NUMBER || null;

    const payload: BrokerCredentialPayload = {
      broker: selectedBroker.id,
      displayName: displayName.trim() || buildDisplayName(selectedBroker, paperOrLive),
      paperOrLive: normalizedModeForBrokerAuthMethod(selectedBroker, authMethod, paperOrLive),
      authMethod,
      externalAccountId,
      externalAccountLabel: externalAccountId,
      providerMetadata,
      replaceExisting: true,
      ...(Object.keys(credentialPayload).length > 0 ? { credentialValues: credentialPayload } : {}),
    };

    try {
      setSaving(true);
      setError(null);
      const token = await getApiToken();
      if (editingId) {
        await updateBrokerCredential(token, editingId, payload);
      } else {
        await createBrokerCredential(token, payload);
      }
      pushEvent({
        event: ANALYTICS_EVENTS.BROKER_CONNECTION_SAVED || 'broker_connection_saved',
        broker: selectedBroker.id,
        authMethod,
        paperOrLive,
        source: 'account_settings',
      });
      await loadItems();
      resetForm();
      setViewMode('list');
      if (returnTo !== SETTINGS_BROKER_CONNECTIONS_PATH) {
        navigate(returnTo);
      } else {
        navigate(SETTINGS_BROKER_CONNECTIONS_PATH, { replace: true });
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to save broker connection'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (item: BrokerCredentialItem): void => {
    setPendingDelete(item);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    try {
      setDeletingId(pendingDelete.id);
      const token = await getApiToken();
      await deleteBrokerCredential(token, pendingDelete.id);
      pushEvent({
        event: ANALYTICS_EVENTS.BROKER_CONNECTION_DELETED || 'broker_connection_deleted',
        broker: pendingDelete.broker,
        source: 'account_settings',
      });
      setPendingDelete(null);
      await loadItems();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete broker connection'));
    } finally {
      setDeletingId(null);
    }
  };

  const reconnectOAuth = async (item: BrokerCredentialItem): Promise<void> => {
    const broker = getBrokerDefinition(item.broker);
    try {
      setError(null);
      setReconnectingId(item.id);
      const token = await getApiToken();
      const response = await startBrokerOAuth(token, item.broker, {
        displayName: item.displayName || buildDisplayName(broker, item.paperOrLive),
        paperOrLive: item.paperOrLive || 'live',
        returnTo,
      }) as BrokerOAuthResponse;
      if (!response?.authorizeUrl) {
        throw new Error('Broker sign-in is not configured for this environment.');
      }
      pushEvent({
        event: 'broker_connection_oauth_started',
        broker: item.broker,
        paperOrLive: item.paperOrLive,
        source: 'account_settings_reconnect',
      });
      window.location.href = response.authorizeUrl;
    } catch (err) {
      setError(getErrorMessage(err, `Unable to start ${broker.name} reconnect.`));
      setReconnectingId(null);
    }
  };

  const showOauthNotice = authMethod === 'oauth';
  const disclosureDisabledReason = acceptedDisclosure
    ? null
    : 'Accept terms to continue.';
  const isChooseStep = flowStep === 'choose' && !editingId;

  const renderSnapshotSummary = (item: BrokerCredentialItem): React.ReactElement => {
    const snapshot = snapshotByCredentialId.get(item.id);
    const { accountValue, cash } = getSnapshotAccountValues(snapshot);

    return (
      <div className="broker-connections-page__snapshot" aria-label={`${getBrokerConnectionTitle(item)} latest account snapshot`}>
        <div className="broker-connections-page__snapshot-row">
          <div className="broker-connections-page__snapshot-metric">
            <span className="broker-connections-page__snapshot-label">
              <FontAwesomeIcon icon={faChartLine} />
              Account value
            </span>
            <span className="broker-connections-page__snapshot-value">{formatMoney(accountValue)}</span>
          </div>
          <div className="broker-connections-page__snapshot-metric">
            <span className="broker-connections-page__snapshot-label">
              <FontAwesomeIcon icon={faWallet} />
              Cash
            </span>
            <span className="broker-connections-page__snapshot-value">{formatMoney(cash, 'No cash yet')}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderConnectionList = (): React.ReactElement => (
    <div className="broker-connections-page__list">
      {loading ? (
        <AccountSettingsLoadingState
          className="broker-connections-page__loading"
          label="Loading broker connections"
          text="Loading broker connections..."
        />
      ) : items.length === 0 ? (
        <section className="broker-connections-page__empty-card">
          <div className="broker-connections-page__empty-copy">
            <div className="broker-connections-page__empty-title">No broker account connected</div>
            <div className="broker-connections-page__empty">Add a paper or live account before deploying a strategy.</div>
          </div>
        </section>
      ) : (
        items.map((item) => {
          const broker = getBrokerDefinition(item.broker);
          const accountLabel = getBrokerAccountLabel(item);
          const itemTitle = getBrokerConnectionTitle(item);
          const needsReconnect = brokerConnectionNeedsAttention(item);
          const connectionStatusLabel = getBrokerConnectionStatusLabel(item);
          const connectionWarning = getBrokerConnectionWarning(item);
          const connectionUpdatedLabel = formatConnectionUpdatedAt(item.connectionUpdatedAt);
          return (
            <article
              key={item.id}
              className={[
                'broker-connections-page__item-row',
                needsReconnect ? 'needs-reconnect' : '',
              ].filter(Boolean).join(' ')}
            >
              <div className="broker-connections-page__item">
                <div className="broker-connections-page__item-main">
                  {renderBrokerAvatar(broker, 'broker-connections-page__avatar broker-connections-page__avatar--list')}
                  <div className="broker-connections-page__item-copy">
                    <div className="broker-connections-page__item-title-row">
                      <div className="broker-connections-page__item-name">
                        <span>{itemTitle}</span>
                        {needsReconnect && <Badge className="broker-connections-page__status-badge" bg="warning" text="dark">{connectionStatusLabel}</Badge>}
                      </div>
                      <BrokerAccountRenameControl
                        className="broker-connections-page__rename"
                        brokerAccountId={item.id}
                        currentName={itemTitle}
                        getApiToken={getApiToken}
                        onSaved={(nextDisplayName) => {
                          setItems((current) => current.map((candidate) => (
                            candidate.id === item.id
                              ? { ...candidate, displayName: nextDisplayName }
                              : candidate
                          )));
                        }}
                      />
                    </div>
                    <div className="broker-connections-page__item-meta">
                      <span>{broker.name}</span>
                      <span>{formatAccountMode(item.paperOrLive)}</span>
                      {accountLabel && <span>Account {accountLabel}</span>}
                      {connectionUpdatedLabel && (
                        <span>
                          <time dateTime={item.connectionUpdatedAt || undefined}>
                            Updated {connectionUpdatedLabel}
                          </time>
                        </span>
                      )}
                    </div>
                    {needsReconnect && (
                      <div className="broker-connections-page__item-warning">
                        {connectionWarning}
                      </div>
                    )}
                  </div>
                </div>
                <div className="broker-connections-page__item-actions">
                  {needsReconnect ? (
                    <Button
                      type="button"
                      variant="warning"
                      size="sm"
                      disabled={reconnectingId === item.id}
                      onClick={() => reconnectOAuth(item)}
                    >
                      {reconnectingId === item.id ? <BootstrapSpinner size="sm" animation="border" /> : `Reconnect ${broker.name}`}
                    </Button>
                  ) : (
                    <Button type="button" onClick={() => beginEdit(item)} variant="outline" size="sm">Update</Button>
                  )}
                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={deletingId === item.id}
                    onClick={() => handleDelete(item)}
                    aria-label={`Delete ${item.displayName || itemTitle}`}
                  >
                    {deletingId === item.id ? <BootstrapSpinner size="sm" animation="border" /> : <FontAwesomeIcon icon={faTrash} />}
                  </Button>
                </div>
              </div>
              {renderSnapshotSummary(item)}
            </article>
          );
        })
      )}
    </div>
  );

  const renderBrokerForm = (): React.ReactElement => (
          <Form className="broker-connections-page__form" onSubmit={handleSubmit}>
            {isChooseStep ? (
              <section className="broker-connections-page__stage-card">
                <div className="broker-connections-page__panel-heading">
                  <h2>Choose a broker</h2>
                  <p>Select account provider.</p>
                </div>

                <div className="broker-connections-page__broker-grid">
                  {brokerCatalog.map((broker) => (
                    <button
                      key={broker.id}
                      type="button"
                      className="broker-connections-page__broker-row"
                      onClick={() => selectBrokerForSetup(broker.id)}
                    >
                      <span className="broker-connections-page__broker-topline">
                        {renderBrokerAvatar(broker)}
                        <span>
                          <span className="broker-connections-page__broker-name">{broker.name}</span>
                          {broker.promoted && <span className="broker-connections-page__broker-promoted">Popular</span>}
                        </span>
                      </span>
                      <span className="broker-connections-page__broker-tags" aria-label="Supported assets">
                        {getBrokerAssetTags(broker).map((tag) => (
                          <span key={tag} className="broker-connections-page__broker-tag">{tag}</span>
                        ))}
                      </span>
                      <span className="broker-connections-page__broker-action" aria-hidden="true">
                        Connect
                        <FontAwesomeIcon icon={faChevronRight} />
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            ) : (
              <section className="broker-connections-page__details-card">
                <div className="broker-connections-page__selected-summary">
                  <div className="broker-connections-page__broker-topline">
                    {renderBrokerAvatar(selectedBroker)}
                    <span>
                      <span className="broker-connections-page__broker-name">{selectedBroker.name}</span>
                      <span className="broker-connections-page__selected-subtitle">
                        {getBrokerAssetTags(selectedBroker).join(' · ')}
                      </span>
                    </span>
                  </div>
                  {!editingId && (
                    <Button type="button" variant="outline" size="sm" onClick={() => setFlowStep('choose')}>
                      Change broker
                    </Button>
                  )}
                </div>

                <div className="broker-connections-page__panel-heading">
                  <h2>Connect your account</h2>
                  <p>{authMethod === 'oauth' ? `Sign in with ${selectedBroker.name}.` : 'Enter broker credentials.'}</p>
                </div>

                {selectedBroker.warning && <Alert variant="warning">{selectedBroker.warning}</Alert>}
                {selectedBroker.referralOffer ? (
                  <aside className="broker-connections-page__referral-offer" aria-label={`${selectedBroker.name} cash bonus offer`}>
                    <div className="broker-connections-page__referral-copy">
                      <span className="broker-connections-page__referral-eyebrow">Partner offer</span>
                      <h3>{selectedBroker.referralOffer.headline}</h3>
                      <p>{selectedBroker.referralOffer.description}</p>
                    </div>
                    <a
                      className="btn broker-connections-page__referral-action"
                      href={selectedBroker.referralOffer.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {selectedBroker.referralOffer.ctaLabel}
                    </a>
                  </aside>
                ) : selectedBroker.referralNote ? (
                  <Alert variant="info">{selectedBroker.referralNote}</Alert>
                ) : null}

                {selectedBroker.authMethods.length > 1 && (
                  <div className="broker-connections-page__method-choice" role="group" aria-label="Connection type">
                    <button
                      type="button"
                      className={authMethod === 'oauth' ? 'active' : ''}
                      onClick={() => setAuthMethod('oauth')}
                    >
                      Broker sign-in
                      <span>Recommended</span>
                    </button>
                    <button
                      type="button"
                      className={authMethod === 'api_key' ? 'active' : ''}
                      onClick={() => setAuthMethod('api_key')}
                    >
                      API keys
                      <span>Manual setup</span>
                    </button>
                  </div>
                )}

                <div className="broker-connections-page__form-grid">
                  <Form.Group controlId="broker-connection-account-type">
                    <Form.Label>Account type</Form.Label>
                    {fixedAccountMode ? (
                      <div className="broker-connections-page__fixed-field">
                        {formatAccountMode(normalizedModeForBrokerAuthMethod(selectedBroker, authMethod, paperOrLive))}
                      </div>
                    ) : (
                      <Form.Select value={paperOrLive} onChange={(event) => setPaperOrLive(event.target.value)}>
                        {availableModes.map((mode) => (
                          <option key={mode} value={mode}>{formatAccountMode(mode)}</option>
                        ))}
                      </Form.Select>
                    )}
                    {selectedBroker.id === 'tradier' && authMethod === 'oauth' && (
                      <Form.Text className="broker-connections-page__field-note">
                        Tradier broker sign-in connects live brokerage accounts. Use API keys for paper accounts.
                      </Form.Text>
                    )}
                  </Form.Group>
                </div>

                <div className="broker-connections-page__nickname-field">
                  <Form.Group>
                    <Form.Label htmlFor="broker-connection-nickname">Nickname (optional)</Form.Label>
                    <Form.Control
                      id="broker-connection-nickname"
                      value={displayName}
                      onChange={(event) => setDisplayName(event.target.value)}
                      placeholder={`${selectedBroker.name} ${paperOrLive}`}
                      maxLength={160}
                    />
                    <Form.Text className="broker-connections-page__field-help">
                      Name this account so it is easy to recognize.
                    </Form.Text>
                  </Form.Group>
                </div>

                {showOauthNotice ? (
                  <>
                    <Form.Check
                      id="broker-connection-disclosure"
                      className="broker-connections-page__disclosure"
                      checked={acceptedDisclosure}
                      onChange={(event) => setAcceptedDisclosure(event.target.checked)}
                      label={(
                        <span>
                          I authorize BotSpot to save this broker connection for reads and deployments. I accept the{' '}
                          <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
                          {' '}and{' '}
                          <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                          .
                        </span>
                      )}
                    />
                    <div className="broker-connections-page__actions broker-connections-page__actions--stacked broker-connections-page__actions--oauth">
                      {disclosureDisabledReason && (
                        <div id="broker-connection-disabled-help" className="broker-connections-page__disabled-help" role="note">
                          {disclosureDisabledReason}
                        </div>
                      )}
                      <span
                        className="broker-connections-page__button-help-wrapper"
                        title={disclosureDisabledReason || undefined}
                      >
                        <Button
                          type="button"
                          className="broker-connections-page__primary-action broker-connections-page__oauth-action"
                          onClick={startOAuthConnection}
                          disabled={saving || !acceptedDisclosure}
                          aria-describedby={disclosureDisabledReason ? 'broker-connection-disabled-help' : undefined}
                        >
                          Sign in with {selectedBroker.name}
                        </Button>
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="broker-connections-page__credentials">
                    {selectedBroker.credentialFields.map((field) => {
                      const revealed = Boolean(revealedSecrets[field.key]);
                      const secretControl = field.secret && !field.multiline;
                      return (
                        <Form.Group key={field.key}>
                          <Form.Label>{field.label}</Form.Label>
                          {field.multiline ? (
                            <Form.Control
                              as="textarea"
                              rows={5}
                              value={credentialValues[field.key] || ''}
                              onChange={(event) => setCredentialValue(field.key, event.target.value)}
                              placeholder={editingId ? 'Leave blank unless rotating this value' : field.label}
                              required={!editingId}
                            />
                          ) : (
                            <div className={secretControl ? 'broker-connections-page__secret-input' : undefined}>
                              {secretControl && (
                                <button
                                  type="button"
                                  className="broker-connections-page__secret-toggle"
                                  onClick={() => toggleSecretReveal(field.key)}
                                  aria-label={`${revealed ? 'Hide' : 'Show'} ${field.label}`}
                                  aria-pressed={revealed}
                                  title={`${revealed ? 'Hide' : 'Show'} ${field.label}`}
                                >
                                  <FontAwesomeIcon icon={revealed ? faEyeSlash : faEye} />
                                </button>
                              )}
                              <Form.Control
                                className={secretControl ? 'broker-connections-page__secret-control' : undefined}
                                type={secretControl && !revealed ? 'password' : 'text'}
                                value={credentialValues[field.key] || ''}
                                onChange={(event) => setCredentialValue(field.key, event.target.value)}
                                placeholder={editingId ? 'Leave blank unless rotating this value' : field.label}
                                required={!editingId}
                              />
                            </div>
                          )}
                          {editingId && (
                            <Form.Text className="broker-connections-page__field-help">
                              Leave blank unless you are replacing this value.
                            </Form.Text>
                          )}
                        </Form.Group>
                      );
                    })}
                  </div>
                )}

                {!showOauthNotice && (
                  <>
                    <Form.Check
                      id="broker-connection-disclosure"
                      className="broker-connections-page__disclosure"
                      checked={acceptedDisclosure}
                      onChange={(event) => setAcceptedDisclosure(event.target.checked)}
                      label={(
                        <span>
                          I authorize BotSpot to save this broker connection for reads and deployments. I accept the{' '}
                          <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
                          {' '}and{' '}
                          <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                          .
                        </span>
                      )}
                    />

                    <div className="broker-connections-page__actions broker-connections-page__actions--stacked">
                      {disclosureDisabledReason && (
                        <div id="broker-connection-disabled-help" className="broker-connections-page__disabled-help" role="note">
                          {disclosureDisabledReason}
                        </div>
                      )}
                      <span
                        className="broker-connections-page__button-help-wrapper"
                        title={disclosureDisabledReason || undefined}
                      >
                        <Button
                          type="submit"
                          className="broker-connections-page__primary-action"
                          disabled={saving || !acceptedDisclosure}
                          aria-describedby={disclosureDisabledReason ? 'broker-connection-disabled-help' : undefined}
                        >
                          {saving ? (
                            <><BootstrapSpinner size="sm" animation="border" /> Saving...</>
                          ) : (
                            <><FontAwesomeIcon icon={editingId ? faCheck : faPlus} /> {editingId ? 'Update connection' : 'Save connection'}</>
                          )}
                        </Button>
                      </span>
                    </div>
                  </>
                )}
              </section>
            )}
          </Form>
  );

  return (
    <div className="broker-connections-page broker-connections-page--settings">
      <div className="settings-subpage-header broker-connections-page__heading">
        <div className="settings-subpage-header-left">
          <h2 className="settings-subpage-title">Broker Connections</h2>
          <p className="settings-subpage-description">
            Manage broker accounts for deployments.
          </p>
        </div>
        <div className="broker-connections-page__heading-action">
          {viewMode === 'form' ? (
            <Button type="button" variant="outline" onClick={closeForm}>
              Back to connections
            </Button>
          ) : !loading ? (
            <Button type="button" onClick={beginAdd} variant="primary" className="broker-connections-page__primary-action">
              <FontAwesomeIcon icon={faPlus} /> Add broker connection
            </Button>
          ) : null}
        </div>
      </div>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {viewMode === 'form' ? renderBrokerForm() : renderConnectionList()}

      <ConfirmationModal
        isOpen={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        title="Delete broker connection"
        message={pendingDelete ? `Delete "${pendingDelete.displayName}"? It will no longer be available for new deployments.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isProcessing={Boolean(deletingId)}
        children={null}
      />
    </div>
  );
}
